import { FC } from 'react';
import {
  Box,
  IconButton,
  Divider,
  Tooltip,
  MenuItem,
  Select,
} from '@mui/material';
import { useCurrentEditor } from '@tiptap/react';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Undo,
  Redo,
  Delete,
} from '@mui/icons-material';
import { grey, red } from '@mui/material/colors';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface MenuBarProps {
  isMini?: boolean;
}

export const MenuBar: FC<MenuBarProps> = ({ isMini = false }) => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Undo" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              sx={{ borderRadius: '5px' }}
            >
              <Undo />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Redo" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              sx={{ borderRadius: '5px' }}
            >
              <Redo />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {!isMini && <Divider orientation="vertical" flexItem />}
      {!isMini && (
        <Box sx={{ display: 'flex', gap: 0.5, color: 'primary' }}>
          <Tooltip title="Format" placement="top">
            <span>
              <Select
                onChange={(e) => {
                  const value = e.target.value;

                  if (value === 'paragraph') {
                    editor.chain().focus().setParagraph().run();
                  } else if (value === 'blockquote') {
                    editor.chain().focus().toggleBlockquote().run();
                  } else if (value === 'h1') {
                    editor.chain().focus().toggleHeading({ level: 1 }).run();
                  }
                }}
                value={(() => {
                  if (editor.isActive('blockquote')) {
                    return 'blockquote';
                  }
                  if (editor.isActive('heading', { level: 1 })) {
                    return 'h1';
                  }
                  return 'paragraph';
                })()}
                sx={{
                  fontWeight: 800,
                  color: grey[600],
                  cursor: 'pointer',
                  height: '30px',
                  '&:hover': {
                    bgcolor: grey[100],

                    '.MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                    },
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
                IconComponent={(props) => (
                  <KeyboardArrowDownIcon {...props} sx={{ color: grey[600] }} />
                )}
              >
                <MenuItem value="h1">Heading</MenuItem>
                <MenuItem value="paragraph">Body</MenuItem>
                <MenuItem value="blockquote">Quote</MenuItem>
              </Select>
            </span>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <Tooltip title="Bold" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleBold().run()}
                color={editor.isActive('bold') ? 'primary' : 'default'}
                sx={{ borderRadius: '5px' }}
              >
                <FormatBold />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Italic" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleItalic().run()}
                color={editor.isActive('italic') ? 'primary' : 'default'}
                sx={{ borderRadius: '5px' }}
              >
                <FormatItalic />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Underline" placement="top">
            <span>
              <IconButton
                size="small"
                onClick={() => editor.chain().focus().toggleUnderline().run()}
                color={editor.isActive('underline') ? 'primary' : 'default'}
                sx={{ borderRadius: '5px' }}
              >
                <FormatUnderlined />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      )}
      <Divider orientation="vertical" flexItem />
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Bullet List" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              color={editor.isActive('bulletList') ? 'primary' : 'default'}
              sx={{ borderRadius: '5px' }}
            >
              <FormatListBulleted />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Ordered List" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              color={editor.isActive('orderedList') ? 'primary' : 'default'}
              sx={{ borderRadius: '5px' }}
            >
              <FormatListNumbered />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box sx={{ flexGrow: 1 }} />
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Clear" placement="top">
          <span>
            <IconButton
              size="small"
              onClick={() => editor.commands.clearContent()}
              sx={{
                borderRadius: '5px',
                '&:hover': { color: red[300] },
              }}
            >
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};
